import React, { useEffect, useMemo, useRef, useState } from 'react';
import { T } from '@tolgee/react';
import { SypacText } from '@sypac/component-library-react';
import Flag from 'react-world-flags';
import { OrderInterface } from '../../OrdersTable/interfaces/Order.interface';
import { motion } from 'framer-motion';

interface DeliveryAddressesProps {
	orders: OrderInterface[];
	forStore?: boolean;
	onAddressSelect?: (payload: {
		address: string;
		countryCode: string;
		lat: number;
		lng: number;
	}) => void;
}

const DeliveryAddresses: React.FC<DeliveryAddressesProps> = ({
	orders,
	forStore = false,
	onAddressSelect,
}) => {
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const [hasVerticalScrollbar, setHasVerticalScrollbar] =
		useState<boolean>(false);
	const [isScrollbarActive, setIsScrollbarActive] = useState<boolean>(false);
	const hideScrollbarTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);

	const deliveryAddresses = useMemo(() => {
		if (!orders?.length) return [];

		const groups = new Map<
			string,
			{
				address: string;
				countryCode?: string;
				deliveries: number;
				lat: number;
				lng: number;
			}
		>();

		for (const order of orders) {
			const address = order.deliveryAddress?.trim();
			const coords = order.deliveryLocation?.coordinates;

			if (!address || !coords || coords.length !== 2) continue;

			const [lng, lat] = coords;
			const key = `${address}|${lng}|${lat}`;

			const existing = groups.get(key);

			if (existing) {
				existing.deliveries += 1;
			} else {
				groups.set(key, {
					address,
					countryCode:
						order.deliveryCountryCode ||
						order.pickupCountryCode ||
						order.product?.country ||
						'POL',
					deliveries: 1,
					lat,
					lng,
				});
			}
		}

		return Array.from(groups.values());
	}, [orders]);

	const handleScroll = () => {
		if (!hasVerticalScrollbar) return;

		setIsScrollbarActive(true);

		if (hideScrollbarTimeoutRef.current) {
			clearTimeout(hideScrollbarTimeoutRef.current);
		}

		hideScrollbarTimeoutRef.current = setTimeout(() => {
			setIsScrollbarActive(false);
		}, 2000);
	};

	useEffect(() => {
		const el = scrollContainerRef.current;
		if (!el) return;

		const checkForScrollbar = () => {
			setHasVerticalScrollbar(el.scrollHeight > el.clientHeight);
		};

		checkForScrollbar();
		window.addEventListener('resize', checkForScrollbar);

		return () => {
			window.removeEventListener('resize', checkForScrollbar);
			if (hideScrollbarTimeoutRef.current) {
				clearTimeout(hideScrollbarTimeoutRef.current);
			}
		};
	}, [deliveryAddresses.length]);

	return forStore ? (
		<div className="flex flex-col gap-5">
			<SypacText variant="body-regular-medium">
				<p className="text-gray-30 text-[14px] leading-[16px]">
					<T keyName="generalTab.recentlyAddresses">Recently addresses:</T>
				</p>
			</SypacText>

			<motion.div
				ref={scrollContainerRef}
				onScroll={handleScroll}
				className="h-full max-h-[494px] flex flex-col gap-[10px] pb-[18px] overflow-x-hidden overflow-y-scroll scroll-smooth scrollbar-custom"
				initial={false}
				animate={{
					paddingRight: hasVerticalScrollbar && isScrollbarActive ? 16 : 0,
				}}
				transition={{ duration: 0.25, ease: 'easeInOut' }}
			>
				{deliveryAddresses.map(
					({ address, countryCode, deliveries, lat, lng }, index) => (
						<div
							key={`${address}-${index}`}
							className="flex items-center justify-between p-[10px] border border-solid border-gray-10 rounded-10 bg-white cursor-pointer transition-all hover:border-gray-30 hover:shadow-dropdown"
							onClick={() =>
								onAddressSelect?.({
									address,
									countryCode: countryCode!,
									lat,
									lng,
								})
							}
						>
							<div className="flex items-center gap-2">
								<span className="mt-[2px]">
									<Flag
										className="object-cover border border-solid border-gray-10 rounded"
										code={countryCode || 'POL'}
										width={20}
										height={14}
									/>
								</span>
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">{address}</p>
								</SypacText>
							</div>

							<div className="flex items-center gap-5">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40">
										<T keyName="generalTab.deliveries">Deliveries:</T>
									</p>
								</SypacText>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-80">{deliveries}</p>
								</SypacText>
							</div>
						</div>
					),
				)}
			</motion.div>
		</div>
	) : (
		<div className="flex flex-col gap-[24px]">
			<div className="flex flex-col gap-4 p-4 bg-white border border-solid border-gray-10 rounded-[12px]">
				<SypacText variant="body-regular-medium">
					<p className="text-gray-60 text-[16px] leading-[22px]">
						<T keyName="generalTab.deliveryAddresses">Delivery addresses</T>
					</p>
				</SypacText>

				<div className="flex flex-col gap-3">
					{deliveryAddresses.map(
						({ address, countryCode, deliveries }, index) => (
							<div
								key={`${address}-${index}`}
								className="flex items-center justify-between p-[10px] border border-solid border-gray-10 rounded-10 bg-[#FAFAFA]"
							>
								<div className="flex items-center gap-2">
									<span className="mt-[2px]">
										<Flag
											className="object-cover border border-solid border-gray-10 rounded"
											code={countryCode || 'POL'}
											width={20}
											height={14}
										/>
									</span>
									<SypacText variant="body-regular-medium">
										<p className="text-gray-80">{address}</p>
									</SypacText>
								</div>

								<div className="flex items-center gap-5">
									<SypacText variant="body-regular-medium">
										<p className="text-gray-40">
											<T keyName="generalTab.deliveries">Deliveries:</T>
										</p>
									</SypacText>

									<SypacText variant="body-regular-medium">
										<p className="text-gray-80">{deliveries}</p>
									</SypacText>
								</div>
							</div>
						),
					)}
				</div>
			</div>
		</div>
	);
};

export default DeliveryAddresses;
